// AI API service for backend OpenAI functionality
// Replaces direct OpenAI calls with backend API calls

interface AIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface StreamResponse {
  onChunk: (chunk: string) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
}

class AIAPIService {
  private baseUrl: string;

  constructor() {
    // Use the same base URL as other API services
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:2024';
  }

  async streamChatCompletion(
    messages: AIMessage[],
    callbacks: StreamResponse,
    temperature: number = 0.7,
    model: string = 'gpt-5.5-2026-04-23'
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/ai/stream-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          messages,
          model,
          temperature
        }),
      });

      if (!response.ok) {
        let detail = '';
        try { 
          const errorData = await response.json();
          detail = errorData.detail || response.statusText;
        } catch {
          detail = response.statusText;
        }
        throw new Error(`AI API error: ${response.status} ${detail}`);
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            callbacks.onComplete();
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          // Process complete lines from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            if (data === '[DONE]') { 
              callbacks.onComplete(); 
              return; 
            }

            try {
              const evt = JSON.parse(data);

              if (evt.type === 'response.output_text.delta') {
                callbacks.onChunk(evt.delta);
              }

              if (evt.type === 'response.completed') {
                callbacks.onComplete();
                return;
              }

              if (evt.type === 'error') {
                callbacks.onError(new Error(evt.error));
                return;
              }
            } catch {
              // Ignore malformed JSON
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('AI API streaming error:', error);
      callbacks.onError(error as Error);
    }
  }

  async streamTenderBankChat(
    messages: AIMessage[],
    callbacks: StreamResponse,
    model: string = 'gpt-5.5-2026-04-23'
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/ai/tender-bank-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          messages,
          model
        }),
      });

      if (!response.ok) {
        let detail = '';
        try { 
          const errorData = await response.json();
          detail = errorData.detail || response.statusText;
        } catch {
          detail = response.statusText;
        }
        throw new Error(`Tender Bank Chat API error: ${response.status} ${detail}`);
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            callbacks.onComplete();
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          // Process complete lines from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            if (data === '[DONE]') { 
              callbacks.onComplete(); 
              return; 
            }

            try {
              const evt = JSON.parse(data);

              if (evt.type === 'response.output_text.delta') {
                callbacks.onChunk(evt.delta);
              }

              if (evt.type === 'response.completed') {
                callbacks.onComplete();
                return;
              }

              if (evt.type === 'error') {
                callbacks.onError(new Error(evt.error));
                return;
              }
            } catch {
              // Ignore malformed JSON
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('Tender Bank Chat streaming error:', error);
      callbacks.onError(error as Error);
    }
  }

// Helper method to convert chat history to AI format
  formatMessages(chatHistory: Array<{ content: string; isUser: boolean }>): AIMessage[] {
    return chatHistory.map(msg => ({
      role: msg.isUser ? 'user' : 'assistant',
      content: msg.content
    }));
  }

  // Compatibility method for existing streamChatWithMCP calls
  async streamChatWithMCP(
    messages: AIMessage[],
    callbacks: StreamResponse
  ): Promise<void> {
    // Use the tender bank chat for richer context
    return this.streamTenderBankChat(messages, callbacks);
  }

  /**
   * Generate a draft response on-demand for a single Awaiting-draft row.
   * Backed by `_draft_single_question` in response_subgraph.py — same
   * prompts, KB retrieval, and scoring as the bulk draft pass, just for one
   * question. Persists the result into `state.drafted_questions` so the
   * Question Matrix row flips `needs_draft → drafted`.
   */
  async draftSingleQuestion(
    threadId: string,
    questionId: string,
  ): Promise<{ draft: any; persisted: boolean; warning?: string }> {
    const response = await fetch(`${this.baseUrl}/ai/draft-single-question`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      },
      body: JSON.stringify({ thread_id: threadId, question_id: questionId }),
    });

    if (!response.ok) {
      let detail = '';
      try {
        const errorData = await response.json();
        detail = errorData.detail || response.statusText;
      } catch {
        detail = response.statusText;
      }
      throw new Error(`Draft API error: ${response.status} ${detail}`);
    }

    return response.json();
  }
}

export const aiApiService = new AIAPIService();
export type { AIMessage, StreamResponse };